import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articlesService } from '../../services/api';
import { 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaSearch
} from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const ArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [programFilter, setProgramFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await articlesService.getAll();
      let data = [];
      if (response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data)) {
          data = response.data;
        }
      }
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!articleToDelete) return;
    try {
      await articlesService.delete(articleToDelete.id);
      await fetchArticles();
      setShowDeleteModal(false);
      setArticleToDelete(null);
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Error during deletion');
    }
  };

  const filteredArticles = Array.isArray(articles) ? articles.filter(art => {
    if (!art) return false;
    const matchesSearch = art.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          art.author?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesProgram = programFilter === 'all' || art.program_id === programFilter;
    return matchesSearch && matchesProgram;
  }) : [];

  const programs = [
    { value: 'all', label: 'All Programs' },
    { value: 'international-mobility', label: 'International Mobility' },
    { value: 'global-citizenship', label: 'Global Citizenship' },
    { value: 'cooperation-development', label: 'Cooperation & Development' },
    { value: 'youth-empowerment', label: 'Youth Empowerment' }
  ];

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Articles Management</h1>
          <p className="mt-1 text-gray-600">{filteredArticles.length} article(s)</p>
        </div>
        <Link
          to="/articles/new"
          className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
        >
          <FaPlus />
          New Article
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Program Filter */}
          <select
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
          >
            {programs.map(prog => (
              <option key={prog.value} value={prog.value}>{prog.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Articles Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {filteredArticles.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p>No articles found</p>
            <Link to="/articles/new" className="mt-4 inline-block text-purple-600 hover:text-purple-800">
              + Create your first article
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredArticles.map((art) => (
                  <tr key={art.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {art.image_url ? (
                        <img 
                          src={art.image_url} 
                          alt={art.title} 
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200 text-gray-400 text-xs">
                          No Image
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{art.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{art.excerpt}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {programs.find(p => p.value === art.program_id)?.label || art.program_id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{art.author}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(art.published_at).toLocaleDateString('en-US')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        art.is_published === '1' || art.is_published === 1
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {art.is_published === '1' || art.is_published === 1 ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <Link to={`/articles/edit/${art.id}`} className="text-green-600 hover:text-green-800" title="Edit">
                          <FaEdit />
                        </Link>
                        <button
                          onClick={() => {
                            setArticleToDelete(art);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && articleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the article <strong>"{articleToDelete.title}"</strong>?
            </p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesList;
